import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './AddFlight.css';

const AddFlight = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Flight Details
    airlineName: '',
    airlineCode: '',
    flightNumber: '',
    flightType: 'domestic',
    status: 'active',
    
    // Route Details
    fromAirport: '',
    toAirport: '',
    departureDate: '',
    departureTime: '',
    arrivalDate: '',
    arrivalTime: '',
    duration: '',
    
    // Aircraft & Class
    aircraftType: '',
    cabinClasses: {
      economy: false,
      business: false,
      first: false
    },
    economySeats: '',
    businessSeats: '',
    firstSeats: '',
    
    // Pricing
    baseFare: '',
    taxes: '',
    serviceFee: '',
    totalPrice: '',
    refundable: 'no',
    
    // Baggage Rules
    carryOnAllowance: '',
    checkedBaggageAllowance: '',
    extraBaggagePrice: ''
  });

  const [errors, setErrors] = useState({});

  // Calculate duration (simplified - in real app would use date/time library)
  const calculateDuration = () => {
    if (formData.departureTime && formData.arrivalTime) {
      // Placeholder calculation
      const dep = formData.departureTime.split(':');
      const arr = formData.arrivalTime.split(':');
      const depMinutes = parseInt(dep[0]) * 60 + parseInt(dep[1]);
      const arrMinutes = parseInt(arr[0]) * 60 + parseInt(arr[1]);
      let diff = arrMinutes - depMinutes;
      if (diff < 0) diff += 24 * 60; // Next day
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      return `${hours}h ${minutes}m`;
    }
    return '';
  };

  // Calculate total price
  const calculateTotalPrice = () => {
    const base = parseFloat(formData.baseFare) || 0;
    const tax = parseFloat(formData.taxes) || 0;
    const fee = parseFloat(formData.serviceFee) || 0;
    return (base + tax + fee).toFixed(2);
  };

  // Update calculated fields
  React.useEffect(() => {
    if (formData.departureTime && formData.arrivalTime) {
      const dep = formData.departureTime.split(':');
      const arr = formData.arrivalTime.split(':');
      const depMinutes = parseInt(dep[0]) * 60 + parseInt(dep[1]);
      const arrMinutes = parseInt(arr[0]) * 60 + parseInt(arr[1]);
      let diff = arrMinutes - depMinutes;
      if (diff < 0) diff += 24 * 60; // Next day
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      const calculatedDuration = `${hours}h ${minutes}m`;
      
      setFormData(prev => ({
        ...prev,
        duration: calculatedDuration
      }));
    }
  }, [formData.departureTime, formData.arrivalTime]);

  React.useEffect(() => {
    if (formData.baseFare || formData.taxes || formData.serviceFee) {
      setFormData(prev => ({
        ...prev,
        totalPrice: calculateTotalPrice()
      }));
    }
  }, [formData.baseFare, formData.taxes, formData.serviceFee]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        cabinClasses: {
          ...prev.cabinClasses,
          [name]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation
    if (!formData.airlineName) newErrors.airlineName = 'Airline name is required';
    if (!formData.airlineCode) newErrors.airlineCode = 'Airline code is required';
    if (!formData.flightNumber) newErrors.flightNumber = 'Flight number is required';
    if (!formData.fromAirport) newErrors.fromAirport = 'From airport is required';
    if (!formData.toAirport) newErrors.toAirport = 'To airport is required';
    if (!formData.departureDate) newErrors.departureDate = 'Departure date is required';
    if (!formData.departureTime) newErrors.departureTime = 'Departure time is required';
    if (!formData.arrivalDate) newErrors.arrivalDate = 'Arrival date is required';
    if (!formData.arrivalTime) newErrors.arrivalTime = 'Arrival time is required';
    if (!formData.aircraftType) newErrors.aircraftType = 'Aircraft type is required';
    if (!Object.values(formData.cabinClasses).some(v => v)) {
      newErrors.cabinClasses = 'At least one cabin class is required';
    }
    if (!formData.baseFare) newErrors.baseFare = 'Base fare is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Placeholder for save flight
      console.log('Saving flight:', formData);
      // Navigate back to flights list
      navigate('/admin/flights');
    }
  };

  const handleCancel = () => {
    navigate('/admin/flights');
  };

  return (
    <AdminLayout pageTitle="Add Flight">
      <div className="admin-add-flight-page">
        {/* Page Title */}
        <div className="admin-add-flight-header">
          <h1 className="admin-add-flight-title">Add Flight</h1>
          <nav className="admin-breadcrumb">
            <button 
              className="admin-breadcrumb-link"
              onClick={() => navigate('/admin/flights')}
            >
              Flights
            </button>
            <span className="admin-breadcrumb-separator">→</span>
            <span className="admin-breadcrumb-current">Add Flight</span>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="admin-flight-form">
          {/* Section 1: Basic Flight Details */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Flight Details</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Airline Name <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="airlineName"
                    value={formData.airlineName}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.airlineName ? 'error' : ''}`}
                    placeholder="e.g., British Airways"
                  />
                  {errors.airlineName && (
                    <span className="admin-form-error">{errors.airlineName}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Airline Code <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="airlineCode"
                    value={formData.airlineCode}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.airlineCode ? 'error' : ''}`}
                    placeholder="e.g., BA"
                    maxLength="3"
                  />
                  {errors.airlineCode && (
                    <span className="admin-form-error">{errors.airlineCode}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Flight Number <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="flightNumber"
                    value={formData.flightNumber}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.flightNumber ? 'error' : ''}`}
                    placeholder="e.g., SF101"
                  />
                  {errors.flightNumber && (
                    <span className="admin-form-error">{errors.flightNumber}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Flight Type <span className="admin-required">*</span>
                  </label>
                  <select
                    name="flightType"
                    value={formData.flightType}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="domestic">Domestic</option>
                    <option value="international">International</option>
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Status <span className="admin-required">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Route Details */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Route</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    From Airport <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="fromAirport"
                    value={formData.fromAirport}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.fromAirport ? 'error' : ''}`}
                    placeholder="e.g., Lagos (LOS)"
                  />
                  {errors.fromAirport && (
                    <span className="admin-form-error">{errors.fromAirport}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    To Airport <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="toAirport"
                    value={formData.toAirport}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.toAirport ? 'error' : ''}`}
                    placeholder="e.g., London (LHR)"
                  />
                  {errors.toAirport && (
                    <span className="admin-form-error">{errors.toAirport}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Departure Date <span className="admin-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="departureDate"
                    value={formData.departureDate}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.departureDate ? 'error' : ''}`}
                  />
                  {errors.departureDate && (
                    <span className="admin-form-error">{errors.departureDate}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Departure Time <span className="admin-required">*</span>
                  </label>
                  <input
                    type="time"
                    name="departureTime"
                    value={formData.departureTime}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.departureTime ? 'error' : ''}`}
                  />
                  {errors.departureTime && (
                    <span className="admin-form-error">{errors.departureTime}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Arrival Date <span className="admin-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="arrivalDate"
                    value={formData.arrivalDate}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.arrivalDate ? 'error' : ''}`}
                  />
                  {errors.arrivalDate && (
                    <span className="admin-form-error">{errors.arrivalDate}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Arrival Time <span className="admin-required">*</span>
                  </label>
                  <input
                    type="time"
                    name="arrivalTime"
                    value={formData.arrivalTime}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.arrivalTime ? 'error' : ''}`}
                  />
                  {errors.arrivalTime && (
                    <span className="admin-form-error">{errors.arrivalTime}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    readOnly
                    className="admin-form-input admin-form-input-readonly"
                    placeholder="Auto-calculated"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Aircraft & Class */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Aircraft & Cabin</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Aircraft Type <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="aircraftType"
                    value={formData.aircraftType}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.aircraftType ? 'error' : ''}`}
                    placeholder="e.g., Boeing 737"
                  />
                  {errors.aircraftType && (
                    <span className="admin-form-error">{errors.aircraftType}</span>
                  )}
                </div>

                <div className="admin-form-group admin-form-group-full">
                  <label className="admin-form-label">
                    Cabin Classes <span className="admin-required">*</span>
                  </label>
                  <div className="admin-checkbox-group">
                    <label className="admin-checkbox-label">
                      <input
                        type="checkbox"
                        name="economy"
                        checked={formData.cabinClasses.economy}
                        onChange={handleInputChange}
                        className="admin-checkbox"
                      />
                      <span>Economy</span>
                    </label>
                    <label className="admin-checkbox-label">
                      <input
                        type="checkbox"
                        name="business"
                        checked={formData.cabinClasses.business}
                        onChange={handleInputChange}
                        className="admin-checkbox"
                      />
                      <span>Business</span>
                    </label>
                    <label className="admin-checkbox-label">
                      <input
                        type="checkbox"
                        name="first"
                        checked={formData.cabinClasses.first}
                        onChange={handleInputChange}
                        className="admin-checkbox"
                      />
                      <span>First</span>
                    </label>
                  </div>
                  {errors.cabinClasses && (
                    <span className="admin-form-error">{errors.cabinClasses}</span>
                  )}
                </div>

                {formData.cabinClasses.economy && (
                  <div className="admin-form-group">
                    <label className="admin-form-label">Economy Seats</label>
                    <input
                      type="number"
                      name="economySeats"
                      value={formData.economySeats}
                      onChange={handleInputChange}
                      className="admin-form-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                )}

                {formData.cabinClasses.business && (
                  <div className="admin-form-group">
                    <label className="admin-form-label">Business Seats</label>
                    <input
                      type="number"
                      name="businessSeats"
                      value={formData.businessSeats}
                      onChange={handleInputChange}
                      className="admin-form-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                )}

                {formData.cabinClasses.first && (
                  <div className="admin-form-group">
                    <label className="admin-form-label">First Seats</label>
                    <input
                      type="number"
                      name="firstSeats"
                      value={formData.firstSeats}
                      onChange={handleInputChange}
                      className="admin-form-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Pricing */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Pricing</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Base Fare (₦) <span className="admin-required">*</span>
                  </label>
                  <input
                    type="number"
                    name="baseFare"
                    value={formData.baseFare}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.baseFare ? 'error' : ''}`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  {errors.baseFare && (
                    <span className="admin-form-error">{errors.baseFare}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Taxes (₦)</label>
                  <input
                    type="number"
                    name="taxes"
                    value={formData.taxes}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Service Fee (₦)</label>
                  <input
                    type="number"
                    name="serviceFee"
                    value={formData.serviceFee}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Total Price (₦)</label>
                  <input
                    type="text"
                    name="totalPrice"
                    value={formData.totalPrice ? `₦${parseFloat(formData.totalPrice).toLocaleString()}` : ''}
                    readOnly
                    className="admin-form-input admin-form-input-readonly"
                    placeholder="Auto-calculated"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Refundable</label>
                  <select
                    name="refundable"
                    value={formData.refundable}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Baggage Rules */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Baggage Allowance</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">Carry-on Allowance</label>
                  <input
                    type="text"
                    name="carryOnAllowance"
                    value={formData.carryOnAllowance}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="e.g., 1 piece (7kg)"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Checked Baggage Allowance</label>
                  <input
                    type="text"
                    name="checkedBaggageAllowance"
                    value={formData.checkedBaggageAllowance}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="e.g., 1 piece (23kg)"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Extra Baggage Price (₦)</label>
                  <input
                    type="number"
                    name="extraBaggagePrice"
                    value={formData.extraBaggagePrice}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-form-button admin-form-button-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-form-button admin-form-button-primary"
            >
              Save Flight
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddFlight;

